import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const ITEM_H = 32;

const TimePicker24h = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState('07');
  const [minutes, setMinutes] = useState('00');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h?.padStart(2, '0') || '07');
      const mInt = parseInt(m || '0', 10);
      setMinutes(String((Math.round(mInt / 5) * 5) % 60).padStart(2, '0'));
    }
  }, [value]);

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownH = 248;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < dropdownH && rect.top > dropdownH;
    setPos({
      top: openAbove ? rect.top + window.scrollY - dropdownH - 4 : rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    });
  }, []);

  const handleOpen = () => {
    calcPos();
    setIsOpen((v) => !v);
  };

  // Auto-scroll to selected value on open
  useEffect(() => {
    if (!isOpen) return;
    const hIdx = parseInt(hours, 10);
    const mIdx = MINUTES.indexOf(minutes);
    if (hoursRef.current) {
      hoursRef.current.scrollTop = Math.max(0, hIdx * ITEM_H - ITEM_H * 2);
    }
    if (minutesRef.current) {
      minutesRef.current.scrollTop = Math.max(0, mIdx * ITEM_H - ITEM_H * 2);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const onScroll = () => calcPos();
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, calcPos]);

  const handleTimeChange = (h, m, closeAfter = false) => {
    const newH = h ?? hours;
    const newM = m ?? minutes;
    setHours(newH);
    setMinutes(newM);
    onChange(`${newH}:${newM}`);
    if (closeAfter) setIsOpen(false);
  };

  const dropdown =
    isOpen &&
    createPortal(
      <div
        ref={dropdownRef}
        style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 9999, width: 180 }}
        className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden select-none"
      >
        <div className="grid grid-cols-2 divide-x divide-gray-100">
          {/* Hours column */}
          <div className="flex flex-col">
            <div className="py-1.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-widest border-b border-gray-100">
              Heure
            </div>
            <div ref={hoursRef} className="h-48 overflow-y-auto overscroll-contain scroll-smooth">
              {HOURS.map((h) => (
                <button
                  key={h}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    handleTimeChange(h, null);
                  }}
                  className={`w-full py-1.5 text-sm text-center transition-colors duration-100 ${
                    hours === h ? 'bg-blue-500 text-white font-semibold' : 'text-gray-700 hover:bg-blue-50'
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>

          {/* Minutes column */}
          <div className="flex flex-col">
            <div className="py-1.5 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-widest border-b border-gray-100">
              Min
            </div>
            <div ref={minutesRef} className="h-48 overflow-y-auto overscroll-contain scroll-smooth">
              {MINUTES.map((m) => (
                <button
                  key={m}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    handleTimeChange(null, m, true);
                  }}
                  className={`w-full py-1.5 text-sm text-center transition-colors duration-100 ${
                    minutes === m ? 'bg-blue-500 text-white font-semibold' : 'text-gray-700 hover:bg-blue-50'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div className={`relative ${className}`} ref={triggerRef}>
      <div
        onPointerDown={handleOpen}
        className={`flex items-center gap-2 border rounded-lg px-3 py-2 bg-white cursor-pointer transition-all select-none ${
          isOpen
            ? 'border-blue-500 ring-2 ring-blue-100 shadow-sm'
            : 'border-gray-300 hover:border-blue-300 hover:shadow-sm'
        }`}
      >
        <Clock className={`w-3.5 h-3.5 shrink-0 transition-colors ${isOpen ? 'text-blue-500' : 'text-gray-400'}`} />
        <span
          className={`text-sm font-semibold tabular-nums transition-colors ${isOpen ? 'text-blue-700' : 'text-gray-700'}`}
        >
          {hours}:{minutes}
        </span>
      </div>
      {dropdown}
    </div>
  );
};

export default TimePicker24h;
